import React, { useEffect, useMemo, useState } from 'react'
import { SearchMd, FilterLines, Stars01 } from '@untitled-ui/icons-react'
import { MyButton, MyDoubleCard } from '@interstellar-component'
import RiskAssessmentTable from '../../../EmployeeDetailsSheet/Tabs/ReportsContent/components/Sidebar/RiskAssessmentTable'
import ListedNumberTable from './ListedNumberTable'
import DiscoveredContactTable from './DiscoveredContactTable'
import STATIC_PHONE_SECTION from './staticPhoneData'

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(String(value).replace(/\//g, '-'))
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

// All four indicators come from contact-intelligence (GetContact-style)
// scoring — not yet wired. BE returns risk_indicators: null. Render the
// labels with "—" placeholders so the panel structure matches Figma.
const PLACEHOLDER_INDICATORS = [
  { label: 'Risk labels detected' },
  { label: 'Label consistency score' },
  { label: 'Longest active number' },
  { label: 'Overall risk' },
]

const PAGE_SIZE = 10

export default function PhoneNumbersTab() {
  const [contentTab, setContentTab] = useState('Listed number')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  // FE route-bend: phone-number data is served statically for every workforce
  // individual. RiskSignalsDetailsSheet still fires the backend fetch (routing
  // + handlers untouched); we simply ignore its `phone_numbers` and render a
  // fixed GetContact-derived snapshot instead. See staticPhoneData.js.
  const phoneSection = STATIC_PHONE_SECTION
  const keyTakeaway = phoneSection?.overview?.key_takeaway
  const indicators = phoneSection?.overview?.risk_indicators || PLACEHOLDER_INDICATORS

  const phoneData = useMemo(() => {
    const listed = phoneSection?.listed_numbers ?? []
    if (!listed.length) return null
    return listed.map((r) => ({
      number: r.number,
      status: r.is_current ? 'Current' : null,
      aging: r.aging_months != null ? `${r.aging_months} months` : '—',
      lastUpdate: formatDate(r.last_update_date),
    }))
  }, [phoneSection?.listed_numbers])

  const discoveredContactData = useMemo(
    () => phoneSection?.discovered_contacts ?? [],
    [phoneSection?.discovered_contacts]
  )

  const isListed = contentTab === 'Listed number'

  // Pagination is FE-side and tab-aware: each sub-tab has its own row source
  // and its own filter predicate (numbers vs. labels). Filtered length feeds
  // the page count; the slice we hand the child is already filtered, so the
  // child's internal `data.filter(includes(""))` becomes a no-op.
  const filteredListed = useMemo(() => {
    const rows = phoneData ?? []
    const q = searchTerm.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => r.number.toLowerCase().includes(q))
  }, [phoneData, searchTerm])

  const filteredDiscovered = useMemo(() => {
    const rows = discoveredContactData ?? []
    const q = searchTerm.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => r.label.toLowerCase().includes(q))
  }, [discoveredContactData, searchTerm])

  const activeFiltered = isListed ? filteredListed : filteredDiscovered
  const totalPages = Math.max(1, Math.ceil(activeFiltered.length / PAGE_SIZE))

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1)
  }, [currentPage, totalPages])

  const pagedListed = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredListed.slice(start, start + PAGE_SIZE)
  }, [filteredListed, currentPage])

  const pagedDiscovered = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredDiscovered.slice(start, start + PAGE_SIZE)
  }, [filteredDiscovered, currentPage])

  return (
    <div className="grid lg:grid-cols-12 gap-8 pt-4">
      <div className="lg:col-span-4 flex flex-col gap-8 h-full lg:border-r lg:border-gray-200 lg:pr-8">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            Overview
            <Stars01 className="h-5 w-5 text-brand/900" />
          </h3>
          <p className="text-sm-regular text-gray-600">
            Key insights from credit and background data.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <MyDoubleCard heading="Key takeaway">
            <p className="text-sm-regular text-gray-600 leading-relaxed">
              {keyTakeaway || 'No phone number insights available yet.'}
            </p>
          </MyDoubleCard>
          <RiskAssessmentTable data={indicators} />
        </div>
      </div>

      <div className="lg:col-span-8 flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {isListed ? 'Associated phone number' : 'Contact labels'}
          </h3>
          <p className="text-sm-regular text-gray-600">
            {isListed
              ? 'List of phone numbers linked to the individual across credit bureau records and external contact sources.'
              : 'Labels used by external contacts to save the individual’s phone numbers.'}
          </p>
        </div>

        <div className="flex flex-col gap-0 border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
          <div className="flex p-4 items-center justify-between border-b border-gray-200 gap-4">
            <div className="relative flex-1 max-w-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchMd className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-white p-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-brand/500 focus:outline-none focus:ring-1 focus:ring-brand/500"
                placeholder={isListed ? 'Search for number' : 'Search'}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <MyButton color="secondary" variant="outlined" size="md">
                <FilterLines className="h-4 w-4" />
                Filters
              </MyButton>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                {['Listed number', 'Discovered contact'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setContentTab(tab)
                      setSearchTerm('')
                      setCurrentPage(1)
                    }}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-all ${
                      contentTab === tab
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {isListed ? (
            phoneData && phoneData.length > 0 ? (
              <ListedNumberTable data={pagedListed} searchTerm="" />
            ) : (
              <div className="px-6 py-10 text-center text-sm-regular text-gray-500">
                No phone numbers linked to this individual.
              </div>
            )
          ) : discoveredContactData.length > 0 ? (
            <DiscoveredContactTable data={pagedDiscovered} searchTerm="" />
          ) : (
            <div className="px-6 py-10 text-center text-sm-regular text-gray-500">
              No discovered contact labels for this individual.
            </div>
          )}

          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm-regular text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <MyButton
                color="secondary"
                variant="outlined"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </MyButton>
              <MyButton
                color="secondary"
                variant="outlined"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </MyButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
